import axios from '~/helpers/orchestration/axiosWithAuthHandling';
import axiosWithCancellation from './axiosWithCancellation';
import { createUuid } from '~/helpers/id';
import { orchestration } from '~/config/public';
import Auction, { AuctionNotFound } from '~/App/shared/interfaces/Auction';
import { AxiosInstance, AxiosPromise, AxiosResponse } from 'axios';
import { getDefaultHeaders } from '~/helpers/orchestration/shared';
import { getGaClientId } from '../client/cookie';
import { VehicleHitsType } from '~/App/shared/types/VehicleHitsType';
import ProcessObject from '~/App/shared/interfaces/ProcessObject';
import { AuctionSearchQuery } from '~/App/shared/interfaces/AuctionSearchQuery';
import { EvolvedSearchParams } from '~/App/shared/interfaces/AuctionSearchParams';
import {
  FavouriteAuction,
  FavouriteAuctionWithAuction
} from '~/App/shared/interfaces/FavouriteAuction';
import { logger } from '~/helpers/logger';

/*
  This is an initializer for an cancellable axios handler, which uses a token
  to decide what network requests to cancel, Thus there needs to be one
  instance per type of network call that we want to cancel, therefore if you
  want another call to be canceled, create another one of these following the
  same pattern.
*/
let searchAuctionsCancellableAxios = null as unknown as (
  ...args: unknown[]
) => AxiosPromise | null;
let lastCancellable = true;

interface AuctionRequest {
  id: string;
  idToken?: string;
  params?: Record<string, unknown>;
}

// Compound color values use an underscore internally (URL + translation key);
// the backend expects spaces (e.g. "dark_blue" -> "dark blue"). Exported for
// unit testing.
export function colorWithSpaces(color: string): string;
export function colorWithSpaces(color: string[]): string[];
export function colorWithSpaces<T>(color: T): T;
export function colorWithSpaces(color: unknown): unknown {
  if (typeof color === 'string') {
    return color.replace(/_/g, ' ');
  }
  if (Array.isArray(color)) {
    return color.map(value =>
      typeof value === 'string' ? value.replace(/_/g, ' ') : value
    );
  }
  return color;
}

type GetAuctionResponse = {
  auction: Auction | AuctionNotFound;
};

const getAuction = async (request: AuctionRequest) => {
  try {
    const response = await axios.get<GetAuctionResponse>(
      `${orchestration.url}/auction/auctions/${request.id}`,
      {
        withCredentials: true,
        params: request.params || {},
        responseType: 'json'
      }
    );

    return response.data.auction;
  } catch (error) {
    logger.warn(error.message, { error });
  }
};

interface AuctionsRequest {
  params?: Record<string, unknown>;
  idToken?: string;
}

type GetAuctionsResponse = {
  auctions: Auction[];
};
const getAuctions = async (request: AuctionsRequest) => {
  return await axios
    .get<GetAuctionsResponse>(`${orchestration.url}/auction/auctions`, {
      withCredentials: true,
      params: request.params || {},
      responseType: 'json'
    })
    .then(response => response.data.auctions);
};

export interface SearchAuctionsRequest {
  idToken: string;
  limit: number;
  offset: number;
  params: EvolvedSearchParams | Partial<AuctionSearchQuery>;
  cancellable: boolean;
  signal?: AbortSignal;
}

export type SearchAuctionsResponse = {
  auctions: Auction[];
  carGuideAuctions?: {
    [index: number]: Auction;
  };
  hits: number;
  total: number;
  vehicleTypeHits: VehicleHitsType;
  params?: EvolvedSearchParams | Partial<AuctionSearchQuery>;
};

export type SearchAuctionsResponseWithMeta = SearchAuctionsResponse & {
  meta: {
    lastSearchWithToken: boolean;
  };
  search: string;
};

const searchAuctions = async (request: SearchAuctionsRequest) => {
  if (
    !searchAuctionsCancellableAxios ||
    lastCancellable !== request.cancellable
  ) {
    searchAuctionsCancellableAxios = await axiosWithCancellation({
      cancellable: request.cancellable
    });
  }

  lastCancellable = request.cancellable;
  const params = {
    ...request.params,
    limit: request.limit,
    offset: request.offset
  };

  // compound color values are stored with underscores internally but the
  // backend expects spaces (e.g. "dark_blue" -> "dark blue")
  if ('color' in params && params.color) {
    params.color = colorWithSpaces(params.color);
  }

  // uuid query param should be added only for orderBy=carguide
  if (params?.orderBy === 'carguide') {
    params['uuid'] = createUuid();
  }

  const headers = await getDefaultHeaders();

  /* Null check. TS doesn't get this :( */
  if (searchAuctionsCancellableAxios === null) {
    return;
  }

  const search = searchAuctionsCancellableAxios as (
    ...args: unknown[]
  ) => Promise<AxiosResponse<SearchAuctionsResponse>>;

  return search({
    signal: request.cancellable && request.signal ? request.signal : undefined,
    url: `${orchestration.url}/auction/search`,
    headers,
    method: 'get',
    params: params || {},
    withCredentials: true,
    responseType: 'json'
  }).then(({ data }) => data);
};

type MyAuctionsResponse = {
  auctions: Auction[];
};
type GetMyAuctionsInput = {
  idToken: string;
  params: Record<string, string>;
};
const getMyAuctions = ({ params, idToken: _idToken }: GetMyAuctionsInput) =>
  axios
    .get<MyAuctionsResponse>(
      `${orchestration.url}/mypages/auctions?limit=100`,
      {
        params: params || {},
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.auctions);

type GetMyAuctionInfoInput = {
  idToken: string;
  auctionId: string | number;
};
const getMyAuctionInfo = ({
  auctionId,
  idToken: _idToken
}: GetMyAuctionInfoInput) =>
  axios
    .get<{ auction: Auction }>(
      `${orchestration.url}/mypages/auctions/${auctionId}`,
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.auction);

export type GetFavouriteAuctionsResponse = {
  favouriteAuctions: FavouriteAuctionWithAuction[];
};
const getFavouriteAuctions = ({
  idToken: _idToken,
  signal
}: {
  idToken: string;
  signal?: AbortSignal;
}) =>
  axios
    .get<GetFavouriteAuctionsResponse>(
      `${orchestration.url}/mypages/favourite-auctions?limit=100`,
      {
        withCredentials: true,
        responseType: 'json',
        signal
      }
    )
    .then(
      ({ data }) =>
        data?.favouriteAuctions.map(favourite => ({
          ...favourite.auctionInformation,
          activeAuction: favourite.activeAuction
        })) ?? []
    );

type FavouriteAuctionArgs = {
  id: string;
  idToken: string;
  config?: { fetcher: AxiosInstance['request']; fetchGAClient: () => void };
};

export type FavouriteResponse = {
  favouriteAuction: FavouriteAuction;
};

const storeFavouriteAuction = ({
  id,
  idToken: _idToken,
  config = { fetcher: axios.request, fetchGAClient: getGaClientId }
}: FavouriteAuctionArgs) =>
  config
    .fetcher<FavouriteResponse>({
      url: `${orchestration.url}/mypages/favourite-auctions`,
      data: {
        favouriteAuction: {
          id: id
        },
        gaClientId: config.fetchGAClient()
      },
      withCredentials: true,
      method: 'post',
      responseType: 'json'
    })
    .then(({ data }) => data);

const removeFavouriteAuction = ({
  id,
  idToken: _idToken,
  config = { fetcher: axios.request, fetchGAClient: getGaClientId }
}: FavouriteAuctionArgs) =>
  config
    .fetcher<{ favouriteAuction: FavouriteResponse['favouriteAuction'] }>({
      url: `${orchestration.url}/mypages/favourite-auctions/${id}`,
      method: 'delete',
      withCredentials: true,
      data: {
        gaClientId: config.fetchGAClient()
      },
      responseType: 'json'
    })
    .then(({ data }) => data.favouriteAuction);

type WithId<T> = T & { id: string };

type PreviewProcessArgs = WithId<{
  jwt: string | null;
}>;

const getPreviewProcessObject = ({
  id,
  jwt
}: PreviewProcessArgs): Promise<ProcessObject> =>
  axios
    .get<{ processObject: ProcessObject }>(
      `${orchestration.url}/auction/process-objects/${id}?jwt=${jwt}`,
      {
        responseType: 'json',
        withCredentials: false
      }
    )
    .then(({ data }) => data.processObject);

type PreviewProcessWithAccessHashArgs = WithId<{
  accessHash: string | null;
}>;

const getPreviewProcessObjectWithAccessHash = ({
  id,
  accessHash
}: PreviewProcessWithAccessHashArgs) =>
  axios
    .get<{ processObject: ProcessObject }>(
      `${orchestration.url}/auction/process-objects/by-access-hash/${id}?accessHash=${accessHash}`,
      {
        responseType: 'json',
        withCredentials: false
      }
    )
    .then(({ data }) => data.processObject);

type PreviewObjectArgs = PreviewProcessArgs & PreviewProcessWithAccessHashArgs;

export const getPreviewObject = ({
  id,
  jwt,
  accessHash
}: PreviewObjectArgs): Promise<ProcessObject> =>
  jwt
    ? getPreviewProcessObject({ id, jwt })
    : getPreviewProcessObjectWithAccessHash({ id, accessHash });

const getWonAuctions = ({ idToken: _idToken }: { idToken: string }) =>
  axios
    .get<{ auctions: string[] }>(`${orchestration.url}/mypages/won_auctions`, {
      method: 'get',
      withCredentials: true,
      responseType: 'json'
    })
    .then(({ data }) => data.auctions);

export {
  getAuction,
  getAuctions,
  getMyAuctions,
  getWonAuctions,
  searchAuctions,
  getMyAuctionInfo,
  getFavouriteAuctions,
  storeFavouriteAuction,
  removeFavouriteAuction,
  getPreviewProcessObject,
  getPreviewProcessObjectWithAccessHash
};
